<template>
  <div class="d-flex flex-column" style="gap: 24px; border-radius: 8px">
    <div class="container-plot" ref="plotContainer">
      <div ref="plotHeader">
        <v-row no-gutters align="center" justify="space-between">
          <div>
            <span class="body-bold-medium neutral-20-dark--text">
              {{ $t('message.pages.analyticsV3.usersPage.users') }}
            </span>
          </div>

          <span class="body-regular-smaller neutral-40-light--text">
            {{
              $t('message.pages.analyticsV3.pages.devices.updatedAgo', {
                time: lastUpdatedMinutes,
              })
            }}
          </span>
        </v-row>
        <span class="body-regular-smaller neutral-40-light--text">
          {{ $t('message.pages.analyticsV3.usersPage.usersDesc') }}
          <a
            class="body-bold-smaller neutral-30-base--text span-link"
            @click="handleGoToAnalyticsArticle"
          >
            {{ $t('message.commom.learnMore') }}
            <v-icon size="12" color="neutral-30-base">mdi-open-in-new</v-icon>
          </a>
        </span>
        <v-row class="mt-6" no-gutters align="center" justify="space-between">
          <div class="d-flex align-center">
            <v-badge class="my-badge-scope" inline color="#3F55EE" />
            <span class="body-regular-smaller neutral-20-dark--text ml-1">
              {{ $t('message.pages.analyticsV3.usersPage.uniqueUsers') }}:
            </span>
            <span class="d-flex align-center justify-center">
              <template v-if="chartLoading">
                <v-skeleton-loader
                  type="text"
                  width="40px"
                  height="28px"
                  class="pt-2 ml-2"
                />
              </template>
              <span class="body-regular-smaller ml-1" v-else>{{
                totalUniqueplay
              }}</span>
            </span>
          </div>
          <div class="d-flex align-center">
            <base-select
              v-model="timeAggregation"
              :items="timeAggregationItems"
              item-text="text"
              item-value="value"
              :menu-props="{ top: false, offsetY: true }"
              dense
              attach
              hide-details
              :style="`width: ${$i18next.language === 'es' ? 169 : 124}px`"
            />
          </div>
        </v-row>
      </div>

      <div class="chart-container">
        <v-skeleton-loader
          v-if="chartLoading"
          type="image"
          height="380px"
          width="100%"
          class="chart-skeleton"
        />
        <apexchart
          v-else
          class="chart-full"
          ref="usersChart"
          height="380"
          width="100%"
          :options="chartOptions"
          :series="chartSeries"
          :key="`${timeAggregation}-${locale}`"
        />
        <apexchart
          class="pdf-export-chart"
          ref="fakeChart"
          height="380"
          width="894"
          :options="pdfOptions"
          :series="chartSeries"
          :key="'fake'"
        />
      </div>
    </div>

    <div v-if="isCapturingPng" class="capture-clone">
      <div ref="chartContainerForCapture" style="width: 100%; height: 100%; background: white; padding: 24px;">
        <div>
          <v-row no-gutters>
            <span class="body-bold-medium" style="color: #282931">
              {{ $t('message.pages.analyticsV3.usersPage.users') }}
            </span>
          </v-row>
          <span class="body-regular-smaller" style="color: #636578">
            {{ $t('message.pages.analyticsV3.usersPage.usersDesc') }}
          </span>
          <v-row class="mt-6" no-gutters>
            <div class="d-flex align-center">
              <v-badge class="my-badge-scope" inline color="#3F55EE" />
              <span class="body-regular-smaller ml-1" style="color: #282931">
                {{ $t('message.pages.analyticsV3.usersPage.uniqueUsers') }}:
              </span>
              <span class="body-regular-smaller ml-1" style="color: #282931">{{ totalUniqueplay }}</span>
            </div>
          </v-row>
        </div>
        <div class="chart-container">
          <apexchart
            v-if="chartSeries.length > 0"
            class="chart-full"
            height="380"
            width="100%"
            :options="pdfOptions"
            :series="chartSeries"
            :key="`${timeAggregation}-${locale}-clone`"
          />
        </div>
      </div>
    </div>

    <div class="container-table">
      <v-input
        class="search-bar"
        v-model="search"
        size="small"
        hide-details
        :placeholder="$t('message.pages.analyticsV3.usersPage.searchUser')"
        @input="handleSearchInput"
        ><template v-slot:prepend-inner>
          <v-icon class="neutral-60--text" size="16"> mdi-magnify </v-icon>
        </template>
      </v-input>

      <BaseTable
        :items="filteredplay"
        :columns="visibleTableColumns"
        :items-per-page="10"
        :show-pagination="true"
        :loading="tableLoading"
        :is-searching="search.length > 0"
        :showActionColumn="true"
        :showTotalLabel="false"
        :totalLabel="totalPlays"
        @clear-search="search = ''"
        @update-visible-columns="currentlyVisibleColumns = $event"
        noPointer
      >
        <template #nameIpCell="{ row }">
          <span class="text-truncate" :title="row.name || row.ip">
            {{ row.name || row.ip }}
          </span>
        </template>
        <template #playsCell="{ row }">
          <span class="body-regular-small">
            {{
              Number(row.plays).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}
          </span>
        </template>
        <template #impressionsCell="{ row }">
          <span class="body-regular-small">
            {{
              Number(row.impressions).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}
          </span>
        </template>
        <template #watchedVideosCountCell="{ row }">
          <span class="body-regular-small">
            {{
              Number(row.watchedVideosCount).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}
          </span>
        </template>
        <template #screenTimeSecondsCell="{ row }">
          <span class="body-regular-small">
            {{ formatScreenTime(row.screenTimeSeconds) }}
          </span>
        </template>
      </BaseTable>
    </div>
  </div>
</template>

<script>
import {
  format,
  parseISO,
  getISOWeek,
  startOfISOWeek,
  endOfISOWeek,
} from 'date-fns'
import { ptBR, enUS, es } from 'date-fns/locale'
import { mapState, mapMutations } from 'vuex'
import VueApexCharts from 'vue-apexcharts'
import BaseTable from '@/components/BaseComponents/BaseTable.vue'
import BaseSelect from '@/components/BaseComponents/BaseSelect.vue'
import newAnalyticsApi from '@/services/newAnalyticsApi'
import { exportToCsv } from '@/utils/csvExporter.js'
import { exportToPng } from '@/utils/pngExporter.js'
import { generatePdfBlob } from '@/utils/pdfExporter.js'
import { dataFormatter } from '@/utils/dataFormatter'
import currencyMixin from '../../mixins/currencyMixin'

export default {
  name: 'Users',
  components: {
    apexchart: VueApexCharts,
    BaseSelect,
    BaseTable,
  },
  mixins: [currencyMixin],
  data() {
    return {
      isCapturingPng: false,
      currentlyVisibleColumns: [],
      chartLoading: false,
      tableLoading: false,
      search: '',
      lastUpdatedAt: 0,
      lastUpdatedTimeout: null,
      detailsDialog: false,
      hasChanges: false,
      play: [],
      aggregatedChartData: {},
      chartSeries: [],
      chartCategories: [],
      timeAggregation: 'daily',
      searchTimeout: null,
      analyticsLinks: {
        pt: 'https://help.pandavideo.com/pt-br/article/como-funcionam-as-metricas-do-analytics-na-panda-video-qk2m59/',
        en: 'https://help.pandavideo.com/en-us/article/how-do-analytics-metrics-work-in-panda-video-kinu6m/',
        es: 'https://help.pandavideo.com/es/article/como-funcionan-las-metricas-de-analytics-en-panda-video-cpkcfv/',
      },
      columnsVisibility: [
        { key: 'ip', visible: true },
        { key: 'plays', visible: true },
        { key: 'impressions', visible: true },
        { key: 'watchedVideosCount', visible: true },
        { key: 'screenTimeSeconds', visible: true },
      ],
      columnsMenu: false,
      columnsMenuX: 0,
      columnsMenuY: 0,
    }
  },
  computed: {
    ...mapState({
      dateRange: (state) => state.analytics.filters.dateRange,
      filterVideos: (state) =>
        state.analytics.filters.filterVideos.selectedVideos,
    }),
    ...mapMutations('analytics', ['UPDATE_ERROR_STATE']),
    timeAggregationItems() {
      return [
        { text: this.$t('message.pages.analyticsV3.daily'), value: 'daily' },
        { text: this.$t('message.pages.analyticsV3.weekly'), value: 'weekly' },
        {
          text: this.$t('message.pages.analyticsV3.monthly'),
          value: 'monthly',
        },
      ]
    },
    locale() {
      return this.$i18next.language
    },
    totalPlays() {
      return this.$t('message.pages.video.metricsPage.plays')
    },
    lastUpdatedMinutes() {
      return Math.floor(this.lastUpdatedAt / 60) || 1
    },
    tableHeaders() {
      return [
        {
          text: this.$t('message.pages.analyticsV3.usersPage.table.nameIp'),
          value: 'ip',
          tooltip: this.$t(
            'message.pages.analyticsV3.usersPage.table.nameIpDesc'
          ),
          align: 'start',
          width: '30%',
        },
        {
          text: this.$t('message.pages.analyticsV3.usersPage.table.plays'),
          value: 'plays',
          tooltip: this.$t(
            'message.pages.analyticsV3.usersPage.table.playsDesc'
          ),
          align: 'end',
          width: '17.5%',
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.usersPage.table.impressions'
          ),
          value: 'impressions',
          tooltip: this.$t(
            'message.pages.analyticsV3.usersPage.table.impressionsDesc'
          ),
          align: 'end',
          width: '17.5%',
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.usersPage.table.watchedVideos'
          ),
          value: 'watchedVideosCount',
          tooltip: this.$t(
            'message.pages.analyticsV3.usersPage.table.watchedVideosDesc'
          ),
          align: 'end',
          width: '17.5%',
        },
        {
          text: this.$t('message.pages.analyticsV3.usersPage.table.screenTime'),
          value: 'screenTimeSeconds',
          tooltip: this.$t(
            'message.pages.analyticsV3.usersPage.table.screenTimeDesc'
          ),
          align: 'end',
          width: '17.5%',
          formatter: (value) => {
            const formattedTime = this.formatScreenTime(value)
            return `="${formattedTime}"`
          },
        },
      ]
    },
    filteredplay() {
      if (!this.search) {
        return this.play
      }
      const searchLower = this.search.toLowerCase()
      return this.play.filter((user) => {
        const name = (user.name || '').toLowerCase()
        const ip = (user.ip || '').toLowerCase()
        return name.includes(searchLower) || ip.includes(searchLower)
      })
    },
    totalUniqueplay() {
      if (!this.chartSeries[0]?.data) return '0'
      const total = this.chartSeries[0].data.reduce(
        (acc, value) => acc + value,
        0
      )
      return total.toLocaleString('pt-BR')
    },
    chartOptions() {
      const textColor = this.$vuetify.theme.currentTheme['neutral-40-light']
      const localeMap = { pt: ptBR, es, en: enUS }

      return {
        chart: {
          type: 'line',
          height: 380,
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'straight', width: 2 },
        colors: ['#3F55EE'],
        xaxis: {
          categories: this.chartCategories,
          labels: {
            style: { colors: textColor, fontFamily: 'Rubik, sans-serif' },
            rotateAlways: this.rotateAlways,
            offsetY: 5,
            formatter: (value) => {
              if (!value) return value
              const locale = localeMap[this.locale] || enUS

              if (this.timeAggregation === 'daily') {
                return dataFormatter(value, locale)
              }
              if (this.timeAggregation === 'weekly') {
                const [year, week] = value.split('-W')
                const firstDay = startOfISOWeek(
                  new Date(year, 0, 1 + (week - 1) * 7)
                )
                const lastDay = endOfISOWeek(firstDay)
                const startFormat = format(firstDay, 'dd')
                const endFormat = format(lastDay, 'dd/MM')

                return `${startFormat} - ${endFormat}`
              }
              if (this.timeAggregation === 'monthly') {
                const monthKeys = [
                  'message.pages.credits.january',
                  'message.pages.credits.february',
                  'message.pages.credits.march',
                  'message.pages.credits.april',
                  'message.pages.credits.may',
                  'message.pages.credits.june',
                  'message.pages.credits.july',
                  'message.pages.credits.august',
                  'message.pages.credits.september',
                  'message.pages.credits.october',
                  'message.pages.credits.november',
                  'message.pages.credits.december',
                ]

                const monthIndex = parseInt(value.split('-')[1], 10) - 1
                const monthKey = monthKeys[monthIndex]
                const monthStr = this.$t(monthKey)
                return monthStr
                  ? monthStr.charAt(0).toUpperCase() + monthStr.slice(1)
                  : value
              }
              return value
            },
          },
          axisBorder: { show: false },
          axisTicks: { show: true },
          tooltip: { enabled: false },
          tickAmount: 30,
        },
        yaxis: {
          labels: {
            style: { colors: textColor, fontFamily: 'Rubik, sans-serif' },
            padding: 5,
            formatter: function (val) {
              if (val % 1 === 0) {
                return val.toFixed(0)
              }
              return ''
            },
          },
          tickAmount: 5,
        },
        tooltip: {
          theme: this.$vuetify.theme.dark ? 'dark' : 'light',
          y: {
            title: {
              formatter: () => {
                return this.$t(
                  'message.pages.analyticsV3.usersPage.uniqueUsers'
                )
              },
            },
          },
        },
        grid: {
          borderColor: this.$vuetify.theme.dark ? '#444' : '#E0E0E0',
          strokeDashArray: 0,
          xaxis: { lines: { show: false } },
        },
      }
    },
    pdfOptions() {
      const textColor = '#636578'
      const localeMap = { pt: ptBR, es, en: enUS }

      return {
        chart: {
          type: 'line',
          height: 380,
          width: 878,
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'straight', width: 2 },
        colors: ['#3F55EE'],
        xaxis: {
          categories: this.chartCategories,
          labels: {
            style: { colors: textColor, fontFamily: 'Rubik, sans-serif' },
            rotateAlways: this.rotateAlways,
            offsetY: 5,
            formatter: (value) => {
              if (!value) return value
              const locale = localeMap[this.locale] || enUS

              if (this.timeAggregation === 'daily') {
                return dataFormatter(value, locale)
              }
              if (this.timeAggregation === 'weekly') {
                const [year, week] = value.split('-W')
                const firstDay = startOfISOWeek(
                  new Date(year, 0, 1 + (week - 1) * 7)
                )
                const lastDay = endOfISOWeek(firstDay)
                const startFormat = format(firstDay, 'dd')
                const endFormat = format(lastDay, 'dd/MM')

                return `${startFormat} - ${endFormat}`
              }
              if (this.timeAggregation === 'monthly') {
                const monthKeys = [
                  'message.pages.credits.january',
                  'message.pages.credits.february',
                  'message.pages.credits.march',
                  'message.pages.credits.april',
                  'message.pages.credits.may',
                  'message.pages.credits.june',
                  'message.pages.credits.july',
                  'message.pages.credits.august',
                  'message.pages.credits.september',
                  'message.pages.credits.october',
                  'message.pages.credits.november',
                  'message.pages.credits.december',
                ]

                const monthIndex = parseInt(value.split('-')[1], 10) - 1
                const monthKey = monthKeys[monthIndex]
                const monthStr = this.$t(monthKey)
                return monthStr
                  ? monthStr.charAt(0).toUpperCase() + monthStr.slice(1)
                  : value
              }
              return value
            },
          },
          axisBorder: { show: false },
          axisTicks: { show: true },
          tooltip: { enabled: false },
          tickAmount: 30,
        },
        yaxis: {
          labels: {
            style: { colors: textColor, fontFamily: 'Rubik, sans-serif' },
            padding: 5,
            formatter: function (val) {
              if (val % 1 === 0) {
                return val.toFixed(0)
              }
              return ''
            },
          },
          tickAmount: 5,
        },
        tooltip: {
          theme: this.$vuetify.theme.dark ? 'dark' : 'light',
          crosshairs: {
            show: false
          },
          y: {
            title: {
              formatter: () => {
                return this.$t(
                  'message.pages.analyticsV3.usersPage.uniqueUsers'
                )
              },
            },
          },
        },
        grid: {
          borderColor: '#E0E0E0',
          strokeDashArray: 0,
          xaxis: { lines: { show: false } },
        },
      }
    },
    visibleTableColumns() {
      return this.tableHeaders.map((col) => ({
        ...col,
        width: 100 / this.tableHeaders.length + '%',
      }))
    },

    isRangeMoreThan30Days() {
      if (!this.dateRange || !this.dateRange.start || !this.dateRange.end)
        return false
      const start = new Date(this.dateRange.start)
      const end = new Date(this.dateRange.end)
      const diff = (end - start) / (1000 * 60 * 60 * 24)
      return diff >= 28
    },
    rotateAlways() {
      return this.isRangeMoreThan30Days && this.timeAggregation === 'daily'
    },
  },

  methods: {
    async fetchData() {
      this.chartLoading = true
      this.tableLoading = true

      try {
        const startDate = format(this.dateRange.start, 'yyyy-MM-dd')
        const endDate = format(this.dateRange.end, 'yyyy-MM-dd')
        const allSelectedVideos = this.filterVideos || []
        const hasSelectedVideos = allSelectedVideos.length > 0
        let aggregatedVideoData = null
        let chartResponse = null

        if (hasSelectedVideos) {
          const REQUESTS_PER_TIME = 10
          let allVideoResponses = []
          for (
            let i = 0;
            i < allSelectedVideos.length;
            i += REQUESTS_PER_TIME
          ) {
            const videoChunk = allSelectedVideos.slice(i, i + REQUESTS_PER_TIME)
            const videoPromisesInChunk = videoChunk.map((video) => {
              return newAnalyticsApi.getSingleVideoAnalytics(
                video.videoId,
                startDate,
                endDate
              )
            })
            const chunkResponses = await Promise.all(videoPromisesInChunk)
            allVideoResponses.push(...chunkResponses)
          }

          const usersResponse = await newAnalyticsApi.getUsersMetrics(
            startDate,
            endDate
          )
          aggregatedVideoData =
            this.aggregateMultipleVideoData(allVideoResponses)
          this.processUsersData(usersResponse.data, allSelectedVideos)
          this.lastUpdatedTimeout = setInterval(() => {
            this.lastUpdatedAt += 1
          }, 1000)
        } else {
          const [accountResponse, usersResponse] = await Promise.all([
            newAnalyticsApi.getAccountMetricsV2(startDate, endDate),
            newAnalyticsApi.getUsersMetrics(startDate, endDate),
          ])

          chartResponse = accountResponse
          this.processUsersData(usersResponse.data, [])
        }

        if (hasSelectedVideos && aggregatedVideoData) {
          this.totalMetrics = aggregatedVideoData.total || {}
          this.aggregatedChartData = this.aggregateChartData(
            aggregatedVideoData.views_data
          )
        } else if (chartResponse && chartResponse.data) {
          this.totalMetrics = chartResponse.data.total || {}
          this.aggregatedChartData = this.aggregateChartData(
            chartResponse.data.views_data
          )
        }
      } catch (e) {
        console.error('Erro geral ao buscar dados:', e)
        this.UPDATE_ERROR_STATE(true)
        this.play = []
        this.aggregatedChartData = { daily: [], weekly: [], monthly: [] }
        this.totalMetrics = {}
      } finally {
        this.chartLoading = false
        this.tableLoading = false
      }
    },

    aggregateMultipleVideoData(videoResponses) {
      const aggregatedData = {
        total: {
          view: 0,
          play: 0,
          unique_view: 0,
          unique_play: 0,
        },
        views_data: {},
      }

      videoResponses.forEach((response, index) => {
        if (!response?.data) {
          console.warn(`Vídeo ${index + 1}: Resposta inválida ou sem dados`)
          return
        }
        const videoData = response.data
        const videoTotal = videoData.total || {}
        aggregatedData.total.view += videoTotal.view || 0
        aggregatedData.total.play += videoTotal.play || 0
        aggregatedData.total.unique_view += videoTotal.unique_view || 0
        aggregatedData.total.unique_play += videoTotal.unique_play || 0
        const videoViewsData = videoData.views_data || {}

        Object.entries(videoViewsData).forEach(([dateTime, stats]) => {
          const date = dateTime.split(' ')[0]
          if (!aggregatedData.views_data[date]) {
            aggregatedData.views_data[date] = {
              view: 0,
              play: 0,
              unique_view: 0,
              unique_play: 0,
            }
          }

          aggregatedData.views_data[date].view += stats.view || 0
          aggregatedData.views_data[date].play += stats.play || 0
          aggregatedData.views_data[date].unique_view += stats.unique_view || 0
          aggregatedData.views_data[date].unique_play += stats.unique_play || 0
        })
      })

      return aggregatedData
    },

    processUsersData(allUsersData, selectedVideos) {
      let filteredData = []
      if (Array.isArray(allUsersData)) {
        const hasSelectedVideos = selectedVideos.length > 0
        if (hasSelectedVideos) {
          const selectedVideoExternalIds = selectedVideos.map((v) =>
            v.videoExternalId.slice(24)
          )
          filteredData = allUsersData.filter((apiUser) => {
            const watchedList = apiUser.videos_watched_list || []
            return watchedList.some((watchedVideoId) =>
              selectedVideoExternalIds.includes(watchedVideoId.toLowerCase())
            )
          })
        } else {
          filteredData = allUsersData
        }
      }

      this.play = filteredData.map((apiUser) => ({
        ip: apiUser.user_ip,
        name: null,
        plays: apiUser.play,
        impressions: apiUser.view,
        watchedVideosCount: (apiUser.videos_watched_list || []).length,
        screenTimeSeconds: apiUser.watch_time,
      }))
    },

    handleSearchInput() {
      this.tableLoading = true

      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      this.searchTimeout = setTimeout(() => {
        this.tableLoading = false
      }, 300)
    },

    aggregateChartData(data) {
      if (!data) return { daily: [], weekly: [], monthly: [] }

      const dailyMap = {}
      const weeklyMap = {}
      const monthlyMap = {}

      for (const dateStr in data) {
        const stats = data[dateStr]
        const date = parseISO(dateStr)

        dailyMap[dateStr] = {
          date: dateStr,
          unique_play:
            (dailyMap[dateStr]?.unique_play || 0) + stats.unique_play,
        }

        const week = getISOWeek(date)
        const year = date.getFullYear()
        const weeklyKey = `${year}-W${String(week).padStart(2, '0')}`
        if (!weeklyMap[weeklyKey]) {
          weeklyMap[weeklyKey] = { date: weeklyKey, unique_play: 0 }
        }
        weeklyMap[weeklyKey].unique_play += stats.unique_play

        const monthlyKey = format(date, 'yyyy-MM')
        if (!monthlyMap[monthlyKey]) {
          monthlyMap[monthlyKey] = { date: monthlyKey, unique_play: 0 }
        }
        monthlyMap[monthlyKey].unique_play += stats.unique_play
      }

      return {
        daily: (() => {
          const start = new Date(this.dateRange.start)
          const end = new Date(this.dateRange.end)
          const days = []
          let d = new Date(start)
          while (d <= end) {
            days.push(format(d, 'yyyy-MM-dd'))
            d.setDate(d.getDate() + 1)
          }
          return days.map((dateStr) => {
            return (
              dailyMap[dateStr] || {
                date: dateStr,
                view: 0,
                play: 0,
                unique_view: 0,
                unique_play: 0,
              }
            )
          })
        })(),
        weekly: Object.values(weeklyMap).sort((a, b) =>
          a.date.localeCompare(b.date)
        ),
        monthly: Object.values(monthlyMap).sort((a, b) =>
          a.date.localeCompare(b.date)
        ),
      }
    },

    buildPlot() {
      const dataForPlot = this.aggregatedChartData[this.timeAggregation]
      if (!dataForPlot) {
        this.chartSeries = []
        this.chartCategories = []
        return
      }

      this.chartSeries = [
        {
          name: this.$t('message.pages.analyticsV3.usersPage.uniqueUsers'),
          data: dataForPlot.map((entry) => entry.unique_play),
        },
      ]
      this.chartCategories = dataForPlot.map((entry) => entry.date)
    },

    toggleColumn(key) {
      const col = this.columnsVisibility.find((c) => c.key === key)
      if (col) col.visible = !col.visible
    },
    isColumnVisible(key) {
      const col = this.columnsVisibility.find((c) => c.key === key)
      return col ? col.visible : false
    },
    openColumnsMenu(e) {
      this.columnsMenuX = e.clientX
      this.columnsMenuY = e.clientY
      this.columnsMenu = true
    },

    formatScreenTime(totalSeconds) {
      const numericSeconds = parseInt(totalSeconds, 10)
      if (isNaN(numericSeconds) || numericSeconds < 0) {
        return '0:00'
      }
      const minutes = Math.floor(numericSeconds / 60)
      const seconds = numericSeconds % 60
      const paddedSeconds = String(seconds).padStart(2, '0')

      return `${minutes}:${paddedSeconds}`
    },
    handleGoToAnalyticsArticle() {
      const articleLink =
        this.analyticsLinks[this.$i18next.resolvedLanguage] ??
        this.analyticsLinks['en']
      window.open(articleLink, '_blank')
    },

    generateAndDownloadCsv() {
      const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
      const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
      exportToCsv(
        `analytics-users-${startDateFormatted}--${endDateFormatted}.csv`,
        this.currentlyVisibleColumns,
        this.filteredplay
      )
    },
    async generateAndDownloadPng() {
      this.isCapturingPng = true
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 1000))
      const elementToCapture = this.$refs.chartContainerForCapture
      if (elementToCapture) {
        try {
          const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
          const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
          const filename = `users-${startDateFormatted}--${endDateFormatted}.png`
          await exportToPng(elementToCapture, filename)
        } catch (error) {
          this.UPDATE_ERROR_STATE(true)
        } finally {
          this.isCapturingPng = false
        }
      } else {
        console.error('Elemento para captura não foi encontrado.')
        this.isCapturingPng = false
      }
    },
    async generatePdf() {
      const chart = this.$refs.fakeChart.chart
      const headers = this.visibleTableColumns.map((col) => col.text)
      const sortedDataForPdf = [...this.filteredplay].sort(
        (a, b) => b.plays - a.plays
      )
      const body = sortedDataForPdf.map((item) => {
        return this.visibleTableColumns.map((col) => {
          if (col.formatter) {
            return col.formatter(item[col.value]).replace(/="?|"?/g, '')
          }
          return item[col.value] || ''
        })
      })

      const chartHeaderData = {
        title: this.$t('message.pages.analyticsV3.usersPage.users'),
        description: this.$t('message.pages.analyticsV3.usersPage.usersDesc'),
        metrics: [
          {
            label:
              this.$t('message.pages.analyticsV3.usersPage.uniqueUsers') + ':',
            value: this.totalUniqueplay,
            color: '#697AF2',
          },
        ],
      }

      const columnStyles = {
        0: {
          cellWidth: 'auto',
          overflow: 'ellipsize',
        },
        1: { cellWidth: 26 },
        2: { cellWidth: 26 },
        3: { cellWidth: 26 },
        4: { cellWidth: 26 },
      }

      const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
      const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
      const filename = `users-${startDateFormatted}--${endDateFormatted}.pdf`

      const pdfBlob = await generatePdfBlob({
        chartHeaderData: chartHeaderData,
        chartInstance: chart,
        tableHeaders: headers,
        tableBody: body,
        columnStyles: columnStyles,
      })

      this.$root.$emit('pdf-ready', { blob: pdfBlob, filename: filename })
    },
  },
  watch: {
    aggregatedChartData() {
      this.buildPlot()
    },
    dateRange() {
      this.fetchData()
    },
    filterVideos() {
      this.fetchData()
    },
    timeAggregation() {
      this.buildPlot()
    },
  },
  mounted() {
    this.fetchData()
    this.$root.$on('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$on('trigger-png-export', this.generateAndDownloadPng)
    this.$root.$on('trigger-pdf-export', this.generatePdf)
  },

  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }
    this.$root.$off('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$off('trigger-png-export', this.generateAndDownloadPng)
    this.$root.$off('trigger-pdf-export', this.generatePdf)
  },
}
</script>

<style lang="scss" scoped>
.container-plot {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  padding: 24px;
  gap: 16px;
  border-radius: 8px;
  background-color: var(--v-neutral-100-white-base);
}

.container-table {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  padding: 24px;
  gap: 16px;
  border-radius: 8px;
  background-color: var(--v-neutral-100-white-base);
}

.chart-full {
  width: 100%;
  height: 300px;
  min-height: 300px;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

.chart-container {
  height: 380px;
  min-height: 380px;
  width: 100%;
  padding-top: 10px;
  padding-bottom: 5px;
}
.chart-skeleton {
  width: 100% !important;
  height: 100% !important;
}
.chart-skeleton ::v-deep .v-skeleton-loader__image {
  height: 380px !important;
  border-radius: 10px;
}

.custom-search-bar {
  ::v-deep .v-label {
    background-color: varFigma(neutral-100-white) !important;
    color: varFigma(neutral-95-lightest) !important;
    padding: 0 4px !important;
  }

  ::v-deep .v-input__icon--prepend-inner .v-icon {
    color: varFigma(neutral-95-lightest) !important;
  }

  &.v-input--is-focused {
    ::v-deep fieldset {
      border-color: varFigma(neutral-60) !important;
    }

    ::v-deep .v-label {
      color: varFigma(neutral-60) !important;
    }
  }

  ::v-deep .v-input__icon--prepend-inner .v-icon {
    color: varFigma(neutral-60) !important;
  }
}

.apexcharts-tooltip {
  background-color: varFigma(neutral-100-white) !important;
  color: varFigma(neutral-20-dark) !important;
  border: 1px solid varFigma(neutral-95-lightest) !important;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.apexcharts-tooltip-title {
  background-color: varFigma(neutral-95-lightest) !important;
  color: varFigma(neutral-20-dark) !important;
  font-weight: 500;
  padding: 8px 12px;
  border-bottom: 1px solid varFigma(neutral-95-lightest) !important;
}

.span-link:hover {
  text-decoration: underline !important;
}

.clickable-rows ::v-deep tbody tr:hover > td {
  background-color: transparent !important;
  cursor: default !important;
}

.container-table ::v-deep .v-data-table__wrapper tbody tr {
  cursor: default !important;
  pointer-events: none !important;
}

.container-table ::v-deep .v-data-table__wrapper tbody tr:hover > td {
  background-color: transparent !important;
}

.pdf-export-chart {
  position: absolute;
  top: -9999px;
  left: -9999px;
  z-index: -1; /* Garante que não interfira com nada */
}

.capture-clone {
  position: absolute;
  top: -9999px;
  left: -9999px;
  pointer-events: none;
  height: 500px;
  width: 1200px; 
  background: white;
  box-sizing: border-box;
}
</style>
